import { apiCalling, apiCallingWithoutToken } from "../apiService";

export function resolveThemeFolder(siteData) {
  if (!siteData) return "DoctorsCabin";

  const is_theme_based = siteData.is_theme_based;
  const project_folder = siteData.project_folder
    ? String(siteData.project_folder).trim()
    : "DoctorsCabin";
  const themeName =
    siteData.themeName != null && siteData.themeName !== ""
      ? String(siteData.themeName).trim()
      : "";

  let folder = "DoctorsCabin";
  if (is_theme_based === false && project_folder && project_folder !== "") {
    folder = project_folder;
  } else if (themeName) {
    folder = themeName;
  }

  if (!siteData.id && siteData.id !== 0) {
    folder = "DoctorsCabin";
  }

  if (!folder || folder === "null" || folder === "undefined") {
    folder = "DoctorsCabin";
  }
  return folder;
}

export function isFooterHospitalObject(h) {
  return (
    h != null &&
    typeof h === "object" &&
    !Array.isArray(h) &&
    ("hospitalName" in h || "city" in h || "cityName" in h || "address" in h)
  );
}

/** Short "G-xx,clarityId" OR long inline &lt;script&gt; with UA-xxx / G-xxx (site 1508 vs 1406). */
export function parseGoogleAnalyticsIds(raw) {
  if (raw == null || raw === "") return { googleId: "", clarityId: "" };
  const s = String(raw).trim();
  if (s.length < 30) {
    const Gdata = s.split(",");
    return {
      googleId: (Gdata[0] || "").trim(),
      clarityId: (Gdata[1] || "").trim(),
    };
  }
  const ua = s.match(/UA-\d{4,12}-\d{1,4}/i);
  if (ua) return { googleId: ua[0], clarityId: "" };
  const g4 = s.match(/G-[A-Z0-9]{4,}/i);
  if (g4) return { googleId: g4[0], clarityId: "" };
  const clarity = s.match(/clarity\.ms\/tag\/([^"'>\s]+)/i);
  return {
    googleId: "",
    clarityId: clarity ? clarity[1] : "",
  };
}

/** CMS may send a single object; theme code expects an array of parent blocks. */
export function normalizeThemeSectionContent(content) {
  if (content == null) return null;
  if (Array.isArray(content)) return content.length ? content : null;
  if (typeof content === "object") return [content];
  return null;
}

/**
 * Same themeSectionName can appear multiple times (e.g. Video x3 on site 1406).
 * Last write used to win in sessionStorage and broke the home. Merge content arrays.
 */
export function mergeThemeSectionListByName(themeList) {
  if (!Array.isArray(themeList)) return [];
  const map = new Map();
  for (let i = 0; i < themeList.length; i++) {
    const item = themeList[i];
    const rawName = item?.themeSectionName;
    if (rawName == null || String(rawName).trim() === "") continue;
    const name = String(rawName).trim();
    const normalized = normalizeThemeSectionContent(item.themeSectionContent);
    if (!normalized) continue;
    if (!map.has(name)) {
      map.set(name, normalized.slice());
    } else {
      map.set(name, map.get(name).concat(normalized));
    }
  }
  return Array.from(map.entries()).map(
    ([themeSectionName, themeSectionContent]) => ({
      themeSectionName,
      themeSectionContent,
    }),
  );
}

export function applyThemeSectionListToSession(themeList) {
  const merged = mergeThemeSectionListByName(themeList);
  for (let i = 0; i < merged.length; i++) {
    const name = merged[i]?.themeSectionName;
    if (name == null || String(name).trim() === "") continue;
    try {
      window.sessionStorage.setItem(
        String(name),
        JSON.stringify(merged[i].themeSectionContent ?? null),
      );
    } catch (e) {
      console.warn("themeSectionList skip:", name, e);
    }
  }
}

/** API often returns hospitalData as an array of locations; footer expects JSON array in session. */
export function applyHospitalFooterToSession(details) {
  if (!details) return;
  const h = details.hospitalData;
  if (Array.isArray(h) && h.length > 0) {
    const rows = h.filter(
      (r) => r && typeof r === "object" && !Array.isArray(r),
    );
    if (rows.length === 0) return;
    window.sessionStorage.setItem("footerhospitalData", JSON.stringify(rows));
    window.sessionStorage.setItem(
      "hospitalLocationCount",
      details.hospitalLocationCount ?? "",
    );
    const h0 = rows[0];
    window.sessionStorage.setItem("footerHospitalName", h0.hospitalName ?? "");
    window.sessionStorage.setItem(
      "footerHospitalcity",
      h0.cityName ?? h0.city ?? "",
    );
    window.sessionStorage.setItem("footerHospitaladdress", h0.address ?? "");
    window.sessionStorage.setItem(
      "footerHospitalstateName",
      h0.stateName ?? "",
    );
    window.sessionStorage.setItem("footerHospitalzipCode", h0.zipCode ?? "");
    return;
  }
  if (isFooterHospitalObject(h)) {
    window.sessionStorage.setItem("footerhospitalData", JSON.stringify(h));
    window.sessionStorage.setItem(
      "hospitalLocationCount",
      details.hospitalLocationCount ?? "",
    );
    window.sessionStorage.setItem("footerHospitalName", h.hospitalName ?? "");
    window.sessionStorage.setItem(
      "footerHospitalcity",
      h.cityName ?? h.city ?? "",
    );
    window.sessionStorage.setItem("footerHospitaladdress", h.address ?? "");
    window.sessionStorage.setItem("footerHospitalstateName", h.stateName ?? "");
    window.sessionStorage.setItem("footerHospitalzipCode", h.zipCode ?? "");
  }
}

export async function getSiteData() {
  const urlParams = new URLSearchParams(window.location.search);
  let querySiteId = urlParams.get("siteid") || urlParams.get("siteId");

  if (!querySiteId) {
    if (window.location.pathname.includes("doctorRegistration")) {
      querySiteId = 1;
      // doctor Registration form
    } else {
      const pathnameMatches =
        window.location.pathname.match(/\/?siteid-([0-9]+)/i);
      if (pathnameMatches && pathnameMatches[1]) {
        querySiteId = pathnameMatches[1];
      }
    }
  }

  const isTargetSite = querySiteId;
  const jsonReq = {
    method: "POST",
    body: JSON.stringify({
      functionName: "getSiteDetails",
      siteId: isTargetSite ? isTargetSite : "",
      siteUrl: isTargetSite ? "" : window.location.host,
    }),
  };

  const data = await apiCallingWithoutToken(jsonReq);

  if (!data || String(data.success) !== "1") {
    throw new Error(
      data?.errorMessage || "getSiteDetails failed — check siteId or API",
    );
  }

  const details = data.siteDetailsData?.[0];
  const siteData = details?.siteData?.[0];

  if (!siteData) {
    throw new Error("Invalid getSiteDetails payload (missing siteData)");
  }

  const folder = resolveThemeFolder(siteData);

  window.sessionStorage.setItem("siteId", String(siteData.id ?? ""));
  window.sessionStorage.setItem("themeId", siteData.themeId ?? "");
  window.sessionStorage.setItem("themeName", siteData.themeName ?? "");
  window.sessionStorage.setItem("themeVersion", "0");
  window.sessionStorage.setItem("homeName", folder);
  window.sessionStorage.setItem("reload", "1");
  window.sessionStorage.setItem(
    "dashboard_html",
    siteData.dashboard_html ?? "",
  );
  window.sessionStorage.setItem(
    "theme_custom_color",
    siteData.theme_custom_color ?? "",
  );
  window.sessionStorage.setItem("contact_emailId", siteData.emailId ?? "");
  window.sessionStorage.setItem("contact_no", siteData.contact_no ?? "");
  window.sessionStorage.setItem("fav_icon", siteData.fav_icon ?? "");
  window.sessionStorage.setItem(
    "seo_description",
    siteData.seo_description ?? "",
  );
  window.sessionStorage.setItem("seo_keywords", siteData.seo_keywords ?? "");
  window.sessionStorage.setItem("seo_title", siteData.seo_title ?? "");
  window.sessionStorage.setItem("slug", siteData.slug ?? "");
  window.sessionStorage.setItem("isIDALogin", siteData.isIDALogin ?? "");
  window.sessionStorage.setItem("siteName", siteData.siteName ?? "");
  window.sessionStorage.setItem(
    "book_appointment_terms",
    siteData.book_appointment_terms ?? "",
  );
  window.sessionStorage.setItem(
    "video_chat_terms",
    siteData.video_chat_terms ?? "",
  );
  window.sessionStorage.setItem(
    "hospital_working_time",
    siteData.hospital_working_time ?? "",
  );
  window.sessionStorage.setItem("site_url", siteData.site_url ?? "");
  window.sessionStorage.setItem("title", siteData.title ?? "");
  window.sessionStorage.setItem("fbPixel", siteData.fbPixel ?? "");
  window.sessionStorage.setItem(
    "advertisementScript",
    siteData.advertisementScript ?? "",
  );
  window.sessionStorage.setItem(
    "termsAndCondition",
    siteData.termsAndCondition ?? "",
  );
  window.sessionStorage.setItem("privacyPolicy", siteData.privacyPolicy ?? "");
  window.sessionStorage.setItem(
    "hasHealthTracker",
    siteData.hasHealthTracker ?? "",
  );
  window.sessionStorage.setItem("siteLogo", siteData.logo_path ?? "");
  window.sessionStorage.setItem(
    "google_review_link",
    siteData.google_review_link ?? "",
  );

  const { googleId, clarityId } = parseGoogleAnalyticsIds(
    siteData.google_analytics_script,
  );
  window.sessionStorage.setItem("clarityId", clarityId);
  window.sessionStorage.setItem("googleId", googleId);

  window.sessionStorage.setItem(
    "groupHospitalAdminId",
    details.groupHospitalAdminData?.[0]?.groupHospitalAdminId ?? "",
  );

  window.sessionStorage.setItem(
    "headerData",
    JSON.stringify(details.headerData ?? null),
  );
  window.sessionStorage.setItem(
    "footerData",
    JSON.stringify(details.footerData ?? null),
  );

  window.sessionStorage.setItem(
    "siteServices",
    JSON.stringify(details.services ?? null),
  );

  applyHospitalFooterToSession(details);

  applyThemeSectionListToSession(details.themeSectionList);

  window.sessionStorage.setItem(
    "doctorDetails",
    JSON.stringify(details.doctorDetails ?? null),
  );

  return siteData.id;
}
